import Phaser from 'phaser';
import { gridToWorld } from '../grid';
import type { Cell, Direction, MoleculeMix, MoleculeType } from '../types';
import { coneOffsetFromPorthole, portholeOffsetFromCell } from '../util/cone';

/**
 * Port kinds:
 * - 'spout': emits molecules into a pipe (source end).
 * - 'receptacle': accepts molecules from a pipe (destination end).
 * - 'bidirectional': both — can emit AND accept. Used for the ceiling
 *   atmosphere port and the lungs' top port (the trachea endpoints). A
 *   pipe between two bidirectional ports is treated as a 'trachea' pipe
 *   (gray segments, single-sprite alternating flow) — see Pipe.specialKind
 *   and TracheaFlowFx.
 */
export type PortKind = 'spout' | 'receptacle' | 'bidirectional';

export interface PortConfig {
  id: string;
  kind: PortKind;
  facing: Direction; // direction the cone points (into the workspace from the wall)
  connectionCell: Cell; // the grid cell where a pipe must start (spout) or end (receptacle)
  carries: MoleculeMix;
  /**
   * Receptacles only. Additional source mixes that should be considered CORRECT
   * (in addition to `carries`). A pipe whose `source.carries` matches the
   * receptacle's primary `carries` OR any entry here counts as CORRECT and can
   * trigger organ activation. Example for a brain that lights up on either full
   * blood or just food+oxygen:
   *   `carries: ['food','oxygen','co2'], acceptsAlso: [['food','oxygen']]`
   * A pipe whose source carries a subset of the UNION of (carries + acceptsAlso)
   * but doesn't match any of them exactly is ACCEPTABLE — flows but doesn't
   * activate and doesn't count toward "level solved."
   */
  acceptsAlso?: MoleculeMix[];
  /**
   * Output spouts only. See `OrganPortDef.dependsOn` for full semantics —
   * input port IDs that must currently be receiving their declared `carries`
   * for this spout to emit. Evaluated every frame by `Connector.update`.
   */
  dependsOn?: string[];
  /**
   * Receptacles only. See `OrganPortDef.acceptsAny` — wildcard accept,
   * `classifyPipe` returns CORRECT regardless of source mix.
   */
  acceptsAny?: boolean;
  /**
   * Output spouts only. See `OrganPortDef.transform` — composition function
   * over current inputs, replaces `dependsOn` + `carries` for emission logic.
   */
  transform?: (inputs: Map<string, MoleculeMix>) => MoleculeMix | null;
  /**
   * Receptacles only. When provided, evaluated every frame against the same
   * organ-local `inputs` map that drives `transform` and dep eval. When it
   * returns false, the port is treated as "broken" — any pipe arriving at it
   * classifies as UNACCEPTABLE (burst on arrival), regardless of source
   * `carries`. Used for organ-prerequisite gating, e.g. the lungs' left blood
   * input bursts on arrival when no diaphragm is castellated-joined below
   * (no 'pulse' arriving at the bottom dep port).
   */
  validIf?: (inputs: Map<string, MoleculeMix>) => boolean;
  /**
   * If true, skip rendering the porthole base + molecule icons (used when the port
   * is mounted on an organ — the organ sprite is the visual "body" and only the
   * cone should protrude from it).
   */
  hideBase?: boolean;
  /**
   * If true, render the porthole base but SKIP the molecule icons inside it.
   * Used for "discovery" spouts where the level wants the student to figure out
   * what the spout emits by wiring it up. Has no effect when `hideBase` is true.
   */
  hideContents?: boolean;
  /**
   * Visual style. 'cone' (default) renders the cone-with-porthole used everywhere.
   * 'castellated' renders a square-wave interlock piece — spout and receptacle
   * pieces share the same cell and their teeth interlock to form one joint.
   */
  shape?: 'cone' | 'castellated';
  /**
   * Cosmetic only — adds 180° to the cone sprite's rotation so the tip points
   * the opposite way from the default for this port's kind/facing. The cone
   * pivots on its wide-end origin, so the wide-end stays put; only the tip
   * flips. No effect on connection-cell hit-testing or pipe routing.
   *
   * Use case: a bidirectional port that visually reads better as a "tube
   * entering the organ" rather than "tube protruding from the organ" — e.g.
   * the lungs' top trachea endpoint.
   */
  coneFlipped?: boolean;
  /**
   * Optional texture key for a decorative "fixture body" drawn behind the cone
   * (e.g. the toilet bowl behind a waste receptacle). When set, the porthole
   * disc and molecule icons are skipped — the fixture art IS the body, with only
   * the cone protruding as the pipe inlet. Currently assumes a bottom-wall
   * (facing 'N') port: the art is anchored top-center just beneath the cone and
   * extends downward toward the wall. See the `Port` constructor and
   * `src/scenes/textures/toilet.ts`.
   */
  decoration?: string;
}

/**
 * Vertical offset (container-local) of a decoration fixture's top edge from the
 * porthole center. Negative lifts the fixture up so the cone pokes out its top.
 * Tuned for the bottom toilet receptacle — see `PortConfig.decoration`.
 */
const DECORATION_TOP_OFFSET = -70;

const MOL_TEXTURE: Record<MoleculeType, string> = {
  food: 'mol-food',
  oxygen: 'mol-oxygen',
  co2: 'mol-co2',
  gruel: 'mol-gruel',
  waste: 'mol-waste',
  pulse: 'mol-pulse', // unused in Port porthole (pulse never shown), declared for type completeness
};

export class Port {
  readonly id: string;
  readonly kind: PortKind;
  readonly facing: Direction;
  readonly connectionCell: Cell;
  readonly carries: MoleculeMix;
  readonly acceptsAlso: MoleculeMix[];
  /** Output spouts only. See `PortConfig.dependsOn`. Empty array if unset. */
  readonly dependsOn: string[];
  /** Receptacles only. See `PortConfig.acceptsAny`. False if unset. */
  readonly acceptsAny: boolean;
  /** Output spouts only. See `PortConfig.transform`. Undefined if unset. */
  readonly transform?: (inputs: Map<string, MoleculeMix>) => MoleculeMix | null;
  /** Receptacles only. See `PortConfig.validIf`. Undefined if unset. */
  readonly validIf?: (inputs: Map<string, MoleculeMix>) => boolean;
  readonly shape: 'cone' | 'castellated';

  // World position of the port's hit target (where pointerdown begins for spouts,
  // or pointerup must land for receptacles). We use the connection cell's center.
  readonly hitX: number;
  readonly hitY: number;

  readonly container: Phaser.GameObjects.Container;
  private cone!: Phaser.GameObjects.Image;

  constructor(scene: Phaser.Scene, cfg: PortConfig) {
    this.id = cfg.id;
    this.kind = cfg.kind;
    this.facing = cfg.facing;
    this.connectionCell = cfg.connectionCell;
    this.carries = cfg.carries;
    this.acceptsAlso = cfg.acceptsAlso ?? [];
    this.dependsOn = cfg.dependsOn ?? [];
    this.acceptsAny = cfg.acceptsAny ?? false;
    this.transform = cfg.transform;
    this.validIf = cfg.validIf;
    this.shape = cfg.shape ?? 'cone';

    const cellCenter = gridToWorld(cfg.connectionCell);
    this.hitX = cellCenter.x;
    this.hitY = cellCenter.y;

    if (cfg.shape === 'castellated') {
      // Castellated port sits exactly on its connection cell. The piece itself IS the
      // visible joint — when a spout and receptacle share the cell, their teeth interlock.
      this.container = scene.add.container(cellCenter.x, cellCenter.y);
      this.container.setDepth(20);
      const tex = cfg.kind === 'spout' ? 'port-castellated-spout' : 'port-castellated-receptacle';
      this.cone = scene.add.image(0, 0, tex);
      // Default texture orientations:
      //   spout      → body at top, teeth point S (down)
      //   receptacle → body at bottom, teeth point N (up)
      // Each kind has its own zero-rotation facing direction.
      const facingAngle: Record<Direction, number> =
        cfg.kind === 'spout' ? { S: 0, W: 90, N: 180, E: 270 } : { N: 0, E: 90, S: 180, W: 270 };
      this.cone.setAngle(facingAngle[cfg.facing]);
      this.container.add(this.cone);
      return;
    }

    // Port base sits OUTSIDE the connection cell. The porthole is `PORTHOLE_RADIUS
    // + CONE_LEN` away from the cell in the OPPOSITE of `facing`, so the cone tip
    // reaches the cell. See src/util/cone.ts for the shared math.
    const ph = portholeOffsetFromCell(cfg.facing);
    this.container = scene.add.container(cellCenter.x + ph.dx, cellCenter.y + ph.dy);
    this.container.setDepth(20);

    // Decorative fixture body (e.g. the toilet) renders first so it sits behind
    // the cone. Anchored top-center just under the cone and growing toward the
    // wall (downward for a bottom receptacle). It replaces the porthole disc.
    if (cfg.decoration) {
      const deco = scene.add.image(0, DECORATION_TOP_OFFSET, cfg.decoration);
      deco.setOrigin(0.5, 0);
      this.container.add(deco);
    }

    if (!cfg.hideBase && !cfg.decoration) {
      const base = scene.add.image(0, 0, 'port-base');
      this.container.add(base);
    }

    this.cone = scene.add.image(0, 0, 'port-cone');
    this.cone.setOrigin(0, 0.5);
    // Bidirectional ports use the spout cone (protruding outward) — visually
    // they're "ready to emit." The trachea visualization happens via single
    // sprites traveling along the (gray) pipe, not via the cone shape.
    // `coneFlipped` swaps the visual style to the OPPOSITE kind: this both
    // rotates the cone 180° AND shifts its wide-end out by CONE_LEN, so the
    // wide-end sits at the connection cell instead of at the porthole edge.
    const baseKind = cfg.kind === 'receptacle' ? 'receptacle' : 'spout';
    const visualKind = cfg.coneFlipped
      ? baseKind === 'spout'
        ? 'receptacle'
        : 'spout'
      : baseKind;
    const conePos = coneOffsetFromPorthole(visualKind, cfg.facing);
    this.cone.setPosition(conePos.x, conePos.y);
    this.cone.setAngle(conePos.angle);
    this.container.add(this.cone);

    if (!cfg.hideBase && !cfg.hideContents && !cfg.decoration) {
      const lensRadius = 22;
      cfg.carries.forEach((mol, idx) => {
        const count = cfg.carries.length;
        const angle = ((Math.PI * 2) / Math.max(count, 3)) * idx - Math.PI / 2;
        const r = count === 1 ? 0 : lensRadius * 0.45;
        const m = scene.add.image(r * Math.cos(angle), r * Math.sin(angle), MOL_TEXTURE[mol]);
        m.setScale(count === 1 ? 1.0 : 0.75);
        this.container.add(m);
      });
    }
  }

  private highlightedHover = false;
  private highlightedDirectLink = false;

  /** Hover highlight from PipeBuilder (drag-over a receptacle). */
  setHighlight(on: boolean): void {
    this.highlightedHover = on;
    this.applyHighlight();
  }

  /** Permanent direct-link highlight (from GameScene when two organ ports align). */
  setDirectLinkHighlight(on: boolean): void {
    this.highlightedDirectLink = on;
    this.applyHighlight();
  }

  private applyHighlight(): void {
    if (this.highlightedHover || this.highlightedDirectLink) {
      this.cone.setTint(0xffeb3b);
    } else {
      this.cone.clearTint();
    }
  }
}
